(() => {
  'use strict';

  if (window.looguPdpGalleryInitialized) return;
  window.looguPdpGalleryInitialized = true;

  const ROOT = '[data-loogu-pdp-gallery]';
  const IMAGE = '.product__media-list > .product__media[data-media-type="image"] > img';
  const SAMPLE_SIZE = 64;
  const cache = new Map();
  const controllers = new WeakMap();

  function sourceOf(image) {
    return image.currentSrc || image.getAttribute('src') || '';
  }

  function sourceInfo(source) {
    try {
      const url = new URL(source, window.location.href);
      const name = decodeURIComponent(url.pathname.split('/').pop() || '');
      const keyUrl = new URL(url);
      keyUrl.searchParams.delete('width');
      return { url, key: keyUrl.href, approved: /^loogu-pdp-/i.test(name), animated: /\.gif$/i.test(name) };
    } catch (_) {
      return null;
    }
  }

  function isWhitePerimeter(pixels) {
    let count = 0;
    let whiteCount = 0;
    let sum = 0;
    let sumSquares = 0;
    let differences = 0;
    let abruptChanges = 0;

    // Four complete nested perimeters: corners alone can accept white-framed scenes.
    for (let inset = 0; inset < 4; inset += 1) {
      const end = SAMPLE_SIZE - 1 - inset;
      const sides = [
        Array.from({ length: end - inset + 1 }, (_, n) => [inset + n, inset]),
        Array.from({ length: end - inset + 1 }, (_, n) => [end, inset + n]),
        Array.from({ length: end - inset + 1 }, (_, n) => [end - n, end]),
        Array.from({ length: end - inset + 1 }, (_, n) => [inset, end - n])
      ];

      for (const side of sides) {
        let sideWhite = 0;
        let previous = null;
        for (const [x, y] of side) {
          const offset = (y * SAMPLE_SIZE + x) * 4;
          const rgb = [pixels[offset], pixels[offset + 1], pixels[offset + 2]];
          const minimum = Math.min(...rgb);
          const maximum = Math.max(...rgb);
          // Transparent edges and visible objects/texture at the edge are ambiguous.
          if (pixels[offset + 3] < 250 || minimum < 240 || maximum - minimum > 6) return false;
          const white = minimum >= 248 && maximum - minimum <= 4;
          if (white) {
            sideWhite += 1;
            whiteCount += 1;
          }
          const luminance = (rgb[0] + rgb[1] + rgb[2]) / 3;
          count += 1;
          sum += luminance;
          sumSquares += luminance * luminance;
          if (previous) {
            const difference = Math.max(...rgb.map((value, channel) => Math.abs(value - previous[channel])));
            if (difference > 12) return false;
            if (difference > 5) abruptChanges += 1;
            differences += 1;
          }
          previous = rgb;
        }
        if (sideWhite / side.length < 0.95) return false;
      }
    }

    const variance = Math.max(0, sumSquares / count - (sum / count) ** 2);
    return whiteCount / count >= 0.95 && variance <= 9 &&
      (differences === 0 || abruptChanges / differences <= 0.01);
  }

  function sampleImage(image) {
    try {
      const canvas = document.createElement('canvas');
      canvas.width = SAMPLE_SIZE;
      canvas.height = SAMPLE_SIZE;
      const context = canvas.getContext('2d', { willReadFrequently: true });
      if (!context) return null;
      context.drawImage(image, 0, 0, SAMPLE_SIZE, SAMPLE_SIZE);
      return isWhitePerimeter(context.getImageData(0, 0, SAMPLE_SIZE, SAMPLE_SIZE).data);
    } catch (_) {
      return null;
    }
  }

  function classification(image, info) {
    if (cache.has(info.key)) return cache.get(info.key);
    // Cross-origin or otherwise unreadable images fail closed, without a new request.
    const white = sampleImage(image) === true;
    cache.set(info.key, white);
    if (cache.size > 256) cache.delete(cache.keys().next().value);
    return white;
  }

  function createController(root) {
    let frame = 0;
    let heightFrame = 0;
    let destroyed = false;
    const abort = new AbortController();
    const gallery = () => root.querySelector(':scope > .product__gallery-container > media-gallery.product__gallery');

    function apply(image, portrait, white) {
      const card = image.parentElement;
      const currentGallery = gallery();
      if (!card || !currentGallery || image.closest('media-gallery') !== currentGallery) return;
      // Native morphs overwrite card attributes, while retaining the image itself.
      card.classList.toggle('loogu-pdp-portrait', portrait);
      card.classList.toggle('loogu-pdp-white', white);
    }

    function updateHeight() {
      if (heightFrame || destroyed) return;
      heightFrame = window.requestAnimationFrame(() => {
        heightFrame = 0;
        if (destroyed || !root.isConnected) return;
        const slider = gallery()?.querySelector('slider-element');
        if (slider && slider.clientWidth > 0) slider.adjustHeight?.();
      });
    }

    function inspect(image, budget) {
      const info = sourceInfo(sourceOf(image));
      if (!info || info.animated) {
        apply(image, false, false);
        return;
      }
      if (info.approved) {
        apply(image, true, false);
        return;
      }
      // Do not force lazy images to load, change their source or delay their first paint.
      if (!image.complete || image.naturalWidth === 0) {
        apply(image, false, false);
        return;
      }
      if (!cache.has(info.key)) {
        if (budget.sampled && performance.now() >= budget.deadline) {
          apply(image, false, false);
          return true;
        }
        budget.sampled = true;
      }
      const white = classification(image, info);
      apply(image, white, white);
    }

    function scan() {
      frame = 0;
      if (destroyed) return;
      if (!root.isConnected) {
        destroy();
        return;
      }
      const currentGallery = gallery();
      if (!currentGallery) return;
      const budget = { deadline: performance.now() + 6, sampled: false };
      let deferred = false;
      currentGallery.querySelectorAll(IMAGE).forEach((image) => {
        if (inspect(image, budget)) deferred = true;
      });
      updateHeight();
      if (deferred) schedule();
    }

    function schedule() {
      if (!frame && !destroyed) frame = window.requestAnimationFrame(scan);
    }

    const observer = new MutationObserver((records) => {
      const currentGallery = gallery();
      if (records.some((record) => {
        if (currentGallery && (record.target === currentGallery || currentGallery.contains(record.target))) return true;
        return record.type === 'childList' && Array.from(record.addedNodes).some((node) =>
          node.nodeType === 1 && (node.matches('media-gallery') || node.querySelector('media-gallery'))
        );
      })) schedule();
    });

    function destroy() {
      if (destroyed) return;
      destroyed = true;
      observer.disconnect();
      abort.abort();
      window.cancelAnimationFrame(frame);
      window.cancelAnimationFrame(heightFrame);
      controllers.delete(root);
    }

    observer.observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'src', 'srcset']
    });
    root.addEventListener('load', (event) => {
      if (event.target instanceof HTMLImageElement && event.target.matches(IMAGE)) schedule();
    }, { capture: true, signal: abort.signal });
    customElements.whenDefined('slider-element').then(updateHeight);
    schedule();
    return { destroy };
  }

  function rootsWithin(scope) {
    return [...(scope.matches?.(ROOT) ? [scope] : []), ...scope.querySelectorAll(ROOT)];
  }

  function initialize(scope = document) {
    rootsWithin(scope).forEach((root) => {
      if (!controllers.has(root)) controllers.set(root, createController(root));
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  } else {
    initialize();
  }
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    rootsWithin(event.target).forEach((root) => controllers.get(root)?.destroy());
  });
})();
